import type { AppClass, ScreenshotRecord } from '../lib/types';

export const COMPASS_LOCALE_STORAGE_KEY = 'compass.locale';

export type CompassLocale = 'zh-CN' | 'en';

export interface CompassCopy {
  languageLabel: string;
  themeLabel: string;
  lightTheme: string;
  darkTheme: string;
  loadingCatalog: string;
  appNotFound: string;
  galleryDescription: string;
  backToGallery: string;
  openPreviewOnly: string;
  livePreview: string;
  launchLiveApp: string;
  running: string;
  stop: string;
  openLiveApp: string;
  previewUnavailable: string;
  productIntroduction: string;
  originalPrompt: string;
  axleApiResources: string;
  baseUrl: string;
  runtimeSmoke: string;
  resource: string;
  path: string;
  operations: string;
  endpoint: string;
  deployPackage: string;
  status: string;
  startCommand: string;
  smokePassed: string;
  smokeStatusUnknown: string;
  qaStatus: string;
  pipeline: string;
  accepted: string;
  failureClass: string;
  screenshots: string;
  artifacts: string;
  open: string;
  missing: string;
  yes: string;
  no: string;
  unknown: string;
  liveReady: string;
  deployReady: string;
  qaFailed: string;
  artifactsOnly: string;
  scoreLabel: (score: number) => string;
  previewFrameTitle: (appName: string) => string;
  liveFrameTitle: (appName: string) => string;
}

const COMPASS_COPY: Record<CompassLocale, CompassCopy> = {
  'zh-CN': {
    languageLabel: '语言',
    themeLabel: '主题',
    lightTheme: '浅色',
    darkTheme: '深色',
    loadingCatalog: '正在加载 Compass catalog...',
    appNotFound: '未找到对应应用。',
    galleryDescription: 'Chariot flow 生成物的展示与交互入口，统一呈现 prompt、产品说明、Harness/Axle/Scribe/Trial/Deploy 结果。',
    backToGallery: '← 返回总览',
    openPreviewOnly: '仅打开预览',
    livePreview: '在线预览',
    launchLiveApp: '启动在线应用',
    running: '运行中',
    stop: '停止',
    openLiveApp: '打开在线应用',
    previewUnavailable: '当前没有可用预览。',
    productIntroduction: '产品介绍',
    originalPrompt: '原始 Prompt',
    axleApiResources: 'Axle API 资源',
    baseUrl: 'Base URL',
    runtimeSmoke: '运行时冒烟',
    resource: '资源',
    path: '路径',
    operations: '操作',
    endpoint: '端点',
    deployPackage: '部署包',
    status: '状态',
    startCommand: '启动命令',
    smokePassed: '冒烟已通过',
    smokeStatusUnknown: '冒烟状态未知',
    qaStatus: 'QA 状态',
    pipeline: '流水线',
    accepted: '已验收',
    failureClass: '失败类型',
    screenshots: '截图',
    artifacts: '产物',
    open: '打开',
    missing: '缺失',
    yes: '是',
    no: '否',
    unknown: '未知',
    liveReady: '可在线交互',
    deployReady: '可部署',
    qaFailed: 'QA 失败',
    artifactsOnly: '仅产物',
    scoreLabel: (score) => `评分：${score}`,
    previewFrameTitle: (appName) => `${appName} 预览`,
    liveFrameTitle: (appName) => `${appName} 在线应用`,
  },
  en: {
    languageLabel: 'Language',
    themeLabel: 'Theme',
    lightTheme: 'Light',
    darkTheme: 'Dark',
    loadingCatalog: 'Loading Compass catalog...',
    appNotFound: 'App not found.',
    galleryDescription: 'A unified catalog and interaction surface for Chariot flow outputs, showing prompts, product docs, and Harness/Axle/Scribe/Trial/Deploy results.',
    backToGallery: '<- Back to gallery',
    openPreviewOnly: 'Open preview only',
    livePreview: 'Live preview',
    launchLiveApp: 'Launch live app',
    running: 'Running',
    stop: 'Stop',
    openLiveApp: 'Open live app',
    previewUnavailable: 'Preview unavailable.',
    productIntroduction: 'Product introduction',
    originalPrompt: 'Original prompt',
    axleApiResources: 'Axle API resources',
    baseUrl: 'Base URL',
    runtimeSmoke: 'Runtime smoke',
    resource: 'Resource',
    path: 'Path',
    operations: 'Operations',
    endpoint: 'Endpoint',
    deployPackage: 'Deploy package',
    status: 'Status',
    startCommand: 'Start command',
    smokePassed: 'Smoke passed',
    smokeStatusUnknown: 'Smoke status unknown',
    qaStatus: 'QA status',
    pipeline: 'Pipeline',
    accepted: 'Accepted',
    failureClass: 'Failure class',
    screenshots: 'Screenshots',
    artifacts: 'Artifacts',
    open: 'Open',
    missing: 'Missing',
    yes: 'Yes',
    no: 'No',
    unknown: 'Unknown',
    liveReady: 'Live ready',
    deployReady: 'Deploy ready',
    qaFailed: 'QA failed',
    artifactsOnly: 'Artifacts only',
    scoreLabel: (score) => `Score: ${score}`,
    previewFrameTitle: (appName) => `${appName} preview`,
    liveFrameTitle: (appName) => `${appName} live app`,
  },
};

const STATUS_TOKEN_LABELS: Record<CompassLocale, Record<string, string>> = {
  'zh-CN': {
    completed: '已完成',
    failed: '失败',
    running: '运行中',
    passed: '已通过',
    starting: '启动中',
    healthy: '健康',
    stopped: '已停止',
    'idle-stopped': '空闲停止',
    yes: '是',
    no: '否',
    unknown: '未知',
  },
  en: {
    completed: 'Completed',
    failed: 'Failed',
    running: 'Running',
    passed: 'Passed',
    starting: 'Starting',
    healthy: 'Healthy',
    stopped: 'Stopped',
    'idle-stopped': 'Idle stopped',
    yes: 'Yes',
    no: 'No',
    unknown: 'Unknown',
  },
};

const VIEWPORT_LABELS: Record<CompassLocale, Record<ScreenshotRecord['viewport'], string>> = {
  'zh-CN': {
    desktop: '桌面',
    tablet: '平板',
    mobile: '手机',
    unknown: '未知',
  },
  en: {
    desktop: 'Desktop',
    tablet: 'Tablet',
    mobile: 'Mobile',
    unknown: 'Unknown',
  },
};

export function getCompassCopy(locale: CompassLocale): CompassCopy {
  return COMPASS_COPY[locale];
}

export function normalizeCompassLocale(input?: string | null): CompassLocale | undefined {
  if (!input) return undefined;
  const normalized = input.trim().replace(/_/g, '-').toLowerCase();
  if (normalized === 'zh' || normalized.startsWith('zh-')) return 'zh-CN';
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en';
  return undefined;
}

export function detectCompassLocale(args: {
  queryLang?: string | null;
  storedLang?: string | null;
  navigatorLanguages?: readonly string[] | undefined;
}): CompassLocale {
  const { queryLang, storedLang, navigatorLanguages } = args;
  const candidates = [
    queryLang,
    storedLang,
    ...(navigatorLanguages ?? []),
  ];
  for (const candidate of candidates) {
    const locale = normalizeCompassLocale(candidate);
    if (locale) return locale;
  }
  return 'zh-CN';
}

export function buildCompassHref(pathname: string, locale: CompassLocale): string {
  const url = new URL(pathname, 'http://compass.local');
  url.searchParams.set('lang', locale);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function getAppStatusBadge(
  appClass: AppClass,
  pipelineStatus: string | undefined,
  locale: CompassLocale,
): { color: string; label: string } {
  const copy = getCompassCopy(locale);
  if (appClass === 'live-ready') return { color: 'green', label: copy.liveReady };
  if (appClass === 'deploy-ready') return { color: 'blue', label: copy.deployReady };
  if (pipelineStatus === 'failed') return { color: 'red', label: copy.qaFailed };
  return { color: 'gray', label: copy.artifactsOnly };
}

export function labelStatusToken(status: string | undefined, locale: CompassLocale): string {
  if (!status) return getCompassCopy(locale).unknown;
  return STATUS_TOKEN_LABELS[locale][status] ?? status;
}

export function labelViewport(viewport: ScreenshotRecord['viewport'], locale: CompassLocale): string {
  return VIEWPORT_LABELS[locale][viewport] ?? viewport;
}
